import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { TabBar, Button } from '@chikara/ui';
import { ActivateCardModal } from '@/features/card/components/ActivateCardModal';
import { PageContent } from '@/shared/components/PageContent';
import { CardAssignedTable } from '@/features/card/components/CardAssignedTable/CardAssignedTable';
import { CardBatchesView } from '@/features/card/components/CardBatchesView/CardBatchesView';
import { useCards } from '@/features/card/hooks/useCard/useCards';
import { isBasic } from '@/shared/config/permissions';
import { useAuth } from '@/features/auth/hooks/useAuth';
import { useAccounts } from '@/features/card/hooks/useCard/useAccounts';
import styles from './CardPage.module.css';
import { t } from '@/i18n';


export default function CardPage() {
  const { userData } = useAuth();
  const basic = isBasic(userData.role);
  const [activeTab, setActiveTab] = useState('asigned');
  const [isActivateModalOpen, setIsActivateModalOpen] = useState(false);

  const location = useLocation();
  const tab = location.state?.tab;

  const { cards, pagination: cardsPagination, refetch: refetchCards, isSpinnerLoading: isCardsLoading } = useCards();
  const { accounts, pagination: accountsPagination, refetch: refetchAccounts, isSpinnerLoading: isAccountsLoading } = useAccounts();

  const isPendingStatus = (status: string) => {
    const s = status?.toString().toLowerCase();
    return s === 'pending_approval' || s === 'pending';
  };

  const tabs = [
    {
      label: t('dashboard.card.tabs.assigned'),
      value: 'asigned',
    },
    ...(!basic ? [
      {
        label: activeTab === 'pending_approval' && accounts.length > 0 && !isAccountsLoading && !isCardsLoading
          ? `${t('dashboard.card.tabs.pending')} (${accounts.length })`
          : t('dashboard.card.tabs.pending'),
        value: 'pending_approval',
      },
      {
        label: t('dashboard.card.tabs.tracking'),
        value: 'traking',
      },
    ] : [])
  ];

  useEffect(() => {
    if (tab) {
      setActiveTab(tab);
    }
  }, [tab]);

  useEffect(() => {
    if (basic) {
      refetchCards(1, 10, undefined);
      return;
    }

    if (activeTab === 'pending_approval') {
      refetchAccounts(1, 10, 'pending_approval');
    } else if (activeTab === 'asigned') {
      refetchCards(1, 10, undefined);
    }
  }, [activeTab, refetchCards, refetchAccounts, basic]);

  const displayedData = activeTab === 'pending_approval' ? accounts : (
    basic ? cards : cards.filter(card => !isPendingStatus(card.status))
  );

  const currentPagination = activeTab === 'pending_approval' ? accountsPagination : cardsPagination;
  const currentRefetch = activeTab === 'pending_approval' ? refetchAccounts : refetchCards;

  return (
    <PageContent 
      title={t('dashboard.card.title')}
    >
      {!basic && (
        <TabBar tabs={tabs} activeTab={activeTab} onTabChange={setActiveTab} />
      )}

      {(activeTab === 'asigned' || activeTab === 'pending_approval') && (
        <div className={styles.content}>
          {cards.length > 0 && activeTab === 'asigned' && (
            <div className={styles.buttonContainer}>
              <Button
                variant="outline"
                size="md"
                onClick={() => setIsActivateModalOpen(true)}
              >
                {t('dashboard.card.actions.activate')}
              </Button>
            </div>
          )}

          <CardAssignedTable
            data={displayedData}
            onActivateCard={() => setIsActivateModalOpen(true)}
            onRefetch={currentRefetch}
            pagination={currentPagination}
            basic={basic}
          />
        </div>
      )}

      {activeTab === 'traking' && (
        <div className={styles.content}>
          <CardBatchesView />
        </div>
      )}

      <ActivateCardModal
        isOpen={isActivateModalOpen}
        onClose={() => setIsActivateModalOpen(false)}
        onSuccess={() => {
          currentRefetch(1, 10, undefined);
          setIsActivateModalOpen(false);
        }}
      />
    </PageContent>
  );
}

export { CardPage };
